import type { ActivityFirestore } from "../../ActivitiesContext";
import type { ActivityViewData, ActivityUIKind } from "../../types";

/** Activity event kind — mirrors backend ActivityEventKind. */
export enum ActivityEventKind {
    AsyncTask = "async_task",
    CIModelChange = "continuous_improvement_model_change"
}

/** Activity event severity — mirrors backend ActivityEventSeverity. */
export enum ActivityEventSeverity {
    NeedsAction = "needs_action",
    Notable = "notable",
    Routine = "routine"
}

/** Audience targeting mode — mirrors backend ActivityAudienceMode. */
export enum ActivityAudienceMode {
    All = "all",
    Permissions = "permissions",
    Users = "users"
}

/** Entity type attached to an activity event — mirrors backend ActivityEntityType. */
export enum ActivityEntityType {
    AsyncTask = "async_task",
    RapidProject = "rapid_project"
}

/** Frontend-friendly shape of an activity_events Firestore document. */
export interface FirestoreActivityEvent {
    id: string;
    kind: ActivityEventKind;
    severity: ActivityEventSeverity;
    entity: { type: ActivityEntityType; id: string; url?: string };
    payload: Record<string, unknown>;
    audience: { mode: ActivityAudienceMode; targets?: string[] };
    owner: string;
    createdAt: any;
    updatedAt: any;
    ttlAt?: any;
}

/**
 * Strategy for converting a specific kind of activity event into
 * ActivityViewData and optionally subscribing to secondary data
 * sources for real-time enrichment.
 *
 * Each activity event kind (e.g. "async_task") gets its own provider
 * implementation in a separate file.
 */
export interface ActivityEventProvider {
    /** Key used by activityUIRegistry to pick the correct view component. */
    uiKind: ActivityUIKind;

    /**
     * Convert an activity event into a UI-ready ActivityViewData.
     * Called on every emit — should be cheap.
     *
     * @param event       The activity event document from Firestore.
     * @param enrichment  Data from subscribeToEnrichment, if available.
     */
    convert(event: FirestoreActivityEvent, enrichment?: unknown): ActivityViewData;

    /**
     * Optional: subscribe to a secondary real-time data source for this
     * event (e.g. an async_tasks document for progress updates).
     *
     * Return an unsubscribe function. ActivityEventSource manages the
     * lifecycle — it will call this when the event appears and unsubscribe
     * when it disappears.
     */
    subscribeToEnrichment?(
        event: FirestoreActivityEvent,
        onData: (data: unknown) => void,
        firestore: ActivityFirestore
    ): () => void;
}
